import { useState, useCallback } from "react";

interface UseZoomOptions {
  /** Minimum zoom level (default: 0.5) */
  min?: number;
  /** Maximum zoom level (default: 3) */
  max?: number;
  /** Zoom step per increment (default: 0.25) */
  step?: number;
  /** Initial zoom level (default: 1) */
  initial?: number;
}

interface UseZoomReturn {
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setZoom: (value: number) => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
}

/**
 * A reusable hook for managing zoom level state.
 *
 * @example
 * const { zoom, zoomIn, zoomOut, resetZoom, canZoomIn, canZoomOut } = useZoom();
 * // Apply to element: <div style={{ zoom }}>...</div>
 */
export function useZoom(options: UseZoomOptions = {}): UseZoomReturn {
  const { min = 0.5, max = 3, step = 0.25, initial = 1 } = options;

  const [zoom, setZoomState] = useState<number>(initial);

  const setZoom = useCallback(
    (value: number) => {
      setZoomState(Math.min(max, Math.max(min, value)));
    },
    [min, max],
  );

  const zoomIn = useCallback(() => {
    setZoomState((prev) => Math.min(max, parseFloat((prev + step).toFixed(2))));
  }, [max, step]);

  const zoomOut = useCallback(() => {
    setZoomState((prev) => Math.max(min, parseFloat((prev - step).toFixed(2))));
  }, [min, step]);

  const resetZoom = useCallback(() => {
    setZoomState(initial);
  }, [initial]);

  return {
    zoom,
    zoomIn,
    zoomOut,
    resetZoom,
    setZoom,
    canZoomIn: zoom < max,
    canZoomOut: zoom > min,
  };
}
